import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, Play } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { media } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PhotoImg } from "./PhotoImg";
import { Reveal, SectionHead } from "./Motion";

export const videoEmbed = (url = "") => {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { provider: "youtube", id: yt[1], embed: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`, thumb: `https://i.ytimg.com/vi/${yt[1]}/maxresdefault.jpg` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { provider: "vimeo", id: vm[1], embed: `https://player.vimeo.com/video/${vm[1]}?autoplay=1`, thumb: null };
  return null;
};

export const videoThumb = (v) => v?.thumbnail_url || (v?.thumbnail_photo?.urls?.display && media(v.thumbnail_photo.urls.display)) || videoEmbed(v?.video_url)?.thumb || "";

const IG_POST = /instagram\.com\/(?:[\w.]+\/)?(p|reel|tv)\/([\w-]+)/;

const InstagramEmbed = ({ url, i }) => {
  const m = url.match(IG_POST);
  const permalink = `https://www.instagram.com/${m[1]}/${m[2]}/`;
  return (
    <div className="w-[326px] shrink-0 snap-start sm:w-[360px]" data-testid={`instagram-embed-${i}`}>
      <blockquote className="instagram-media" data-instgrm-permalink={`${permalink}?utm_source=ig_embed`} data-instgrm-version="14"
        style={{ background: "#fff", border: 0, margin: 0, minWidth: 326, width: "100%" }}>
        <a href={permalink} target="_blank" rel="noreferrer" className="block p-6 text-center text-sm text-black/70">View this post on Instagram</a>
      </blockquote>
    </div>
  );
};

const useInstagramEmbeds = (count) => {
  useEffect(() => {
    if (!count) return undefined;
    const run = () => window.instgrm?.Embeds?.process();
    if (window.instgrm) { run(); return undefined; }
    let s = document.getElementById("ig-embed-js");
    if (!s) {
      s = document.createElement("script");
      s.id = "ig-embed-js";
      s.async = true;
      s.src = "https://www.instagram.com/embed.js";
      document.body.appendChild(s);
    }
    s.addEventListener("load", run);
    return () => s.removeEventListener("load", run);
  }, [count]);
};

export const InstagramStrip = ({ light = true }) => {
  const { t, settings } = useLang();
  const { data } = useFetch("/content/instagram");
  const posts = Array.isArray(data) ? data : [];
  const tiles = posts.filter((p) => p.photo || p.image_url);
  const embeds = posts.filter((p) => !p.photo && !p.image_url && IG_POST.test(p.permalink || ""));
  useInstagramEmbeds(embeds.length);
  const handle = settings.instagram_handle || "";
  const profile = `https://www.instagram.com/${handle}/`;
  return (
    <section data-testid="instagram-section" className={light ? "bg-[#f2f1ec] text-[#0b0b0b]" : "bg-[#0b0b0b] text-white"}>
      <div className="mx-auto max-w-[1600px] px-5 py-20 md:px-10 md:py-28">
        <Reveal className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className={`kicker mb-3 ${light ? "!text-black/55" : ""}`}><Instagram size={12} className="mr-2 inline" />@{handle}</div>
            <h2 className="font-display text-4xl font-bold sm:text-5xl">{t("followTitle")}</h2>
            <p className={`mt-3 text-sm ${light ? "text-black/65" : "text-white/65"}`}>{t("followLead")}</p>
          </div>
          <a href={profile} target="_blank" rel="noreferrer" data-testid="instagram-follow-button"
            className={`btn-line ${light ? "!border-black !text-black hover:!text-white [&::before]:!bg-black" : ""}`}><Instagram size={14} /> {t("follow")} <ArrowRight size={14} /></a>
        </Reveal>
        {embeds.length > 0 && (
          <div className="no-scrollbar -mx-5 mb-6 flex snap-x gap-4 overflow-x-auto px-5 pb-2 md:mx-0 md:px-0" data-testid="instagram-embeds" data-lenis-prevent>
            {embeds.map((p, i) => <InstagramEmbed key={p.id} url={p.permalink} i={i} />)}
          </div>
        )}
        {!posts.length && data && (
          <a href={profile} target="_blank" rel="noreferrer" data-testid="instagram-empty-cta"
            className={`group flex items-center justify-between gap-6 border p-8 transition-colors md:p-12 ${light ? "border-black/15 hover:border-black" : "border-white/15 hover:border-white"}`}>
            <span className="font-display min-w-0 truncate text-2xl font-bold sm:text-5xl">@{handle}</span>
            <Instagram size={36} strokeWidth={1.2} className="shrink-0 transition-transform duration-500 group-hover:scale-110" />
          </a>
        )}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {tiles.map((p, i) => (
            <motion.a key={p.id} href={p.permalink || profile} target="_blank" rel="noreferrer" data-testid={`instagram-post-${i}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ delay: i * 0.06, duration: 0.7 }}
              className="group relative aspect-square overflow-hidden">
              {p.photo ? <PhotoImg photo={p.photo} sizes="(max-width:640px) 50vw, 16vw" className="h-full w-full [&>img:last-child]:transition-transform [&>img:last-child]:duration-700 group-hover:[&>img:last-child]:scale-110" />
                : <img src={p.image_url} alt={p.caption || "Instagram"} loading="lazy" className="h-full w-full object-cover" />}
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-[opacity,background-color] duration-500 group-hover:bg-black/45 group-hover:opacity-100">
                <Instagram className="text-white" size={22} />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export const BlogCard = ({ post, i = 0 }) => {
  const { t, L, lang } = useLang();
  return (
    <Reveal delay={i * 0.08}>
      <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="group block border border-white/10 bg-[#111] transition-colors duration-500 hover:border-white/30">
        <div className="relative aspect-[4/3] overflow-hidden">
          {post.cover_photo ? <PhotoImg photo={post.cover_photo} sizes="(max-width:768px) 100vw, 33vw" className="h-full w-full [&>img:last-child]:transition-transform [&>img:last-child]:duration-[1200ms] group-hover:[&>img:last-child]:scale-105" />
            : post.cover_url ? <img src={post.cover_url} alt="" className="h-full w-full object-cover" loading="lazy" /> : <div className="h-full bg-[#1a1a1a]" />}
        </div>
        <div className="p-6">
          <div className="kicker !text-[10px] !text-white/55">{new Date(post.published_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "short", year: "numeric" })} · {t(post.category)}</div>
          <h3 className="font-display mt-3 text-xl font-bold leading-tight text-white">{L(post, "title")}</h3>
          <p className="mt-3 line-clamp-2 text-sm text-white/60">{L(post, "excerpt")}</p>
          <span className="kicker mt-5 inline-flex items-center gap-2 !text-white">{t("readMore")} <ArrowRight size={12} className="transition-transform duration-500 group-hover:translate-x-1" /></span>
        </div>
      </Link>
    </Reveal>
  );
};

export const FromBlog = () => {
  const { t } = useLang();
  const { data } = useFetch("/content/posts?limit=3");
  const posts = Array.isArray(data) ? data : [];
  if (!posts.length) return null;
  return (
    <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-32" data-testid="home-blog-section">
      <SectionHead title={t("fromBlog")} lead={t("blogLead")} action={<Link to="/blog" data-testid="home-blog-view-all" className="btn-line self-start">{t("viewAll")} <ArrowRight size={14} /></Link>} />
      <div className="grid gap-5 md:grid-cols-3">{posts.map((p, i) => <BlogCard key={p.id} post={p} i={i} />)}</div>
    </section>
  );
};

export const FeaturedVideo = ({ onPlay, videoId }) => {
  const { t, L } = useLang();
  const { data } = useFetch("/content/videos");
  const v = useMemo(() => (Array.isArray(data) ? data.find((x) => videoId && x.id === videoId) || data.find((x) => x.featured) || data[0] : null), [data, videoId]);
  const [hover, setHover] = useState(false);
  if (!v) return null;
  return (
    <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-32" data-testid="featured-video-section">
      <div className="grid items-center gap-10 lg:grid-cols-12">
        <Reveal className="lg:col-span-8">
          <button onClick={() => onPlay?.(v)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} data-testid="featured-video-play"
            className="group relative block aspect-video w-full overflow-hidden bg-[#111]">
            {videoThumb(v) && <img src={videoThumb(v)} alt={L(v, "title")} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1600ms] group-hover:scale-105" />}
            <div className="absolute inset-0 bg-black/30 transition-colors duration-500 group-hover:bg-black/10" />
            <motion.span animate={{ scale: hover ? 1.1 : 1 }} className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-black/30 backdrop-blur-md">
              <Play className="ml-1 text-white" size={26} fill="white" />
            </motion.span>
          </button>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-4">
          <div className="kicker mb-4">{t("featuredVideo")} · {t("documentary")}</div>
          <h2 className="font-display text-4xl font-bold leading-[0.95] text-white sm:text-5xl" data-testid="featured-video-title">{L(v, "title")}</h2>
          <p className="mt-5 text-white/70">{L(v, "description")}</p>
          <button onClick={() => onPlay?.(v)} data-testid="featured-video-watch-button" className="btn-line mt-8">{t("watch")} <ArrowRight size={14} /></button>
        </Reveal>
      </div>
    </section>
  );
};
